import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Envelope, Lock, User, ArrowRight } from "@phosphor-icons/react";
import { AppFrame } from "../components/Layout";
import { useAuth } from "../lib/auth";
import { getJSON, setJSON, K } from "../lib/storage";

const fmtMMSS = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(location.pathname === "/register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockLeft, setLockLeft] = useState(0);

  useEffect(() => {
    if (lockLeft <= 0) return;
    const id = setInterval(() => setLockLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [lockLeft]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "register") {
        const prof = getJSON(K.profile, {});
        const u = await register(email, password, name || prof.name || "", prof.term ?? null);
        setJSON(K.profile, { ...prof, name: u.name, term: u.term });
      } else {
        const u = await login(email, password);
        const prof = getJSON(K.profile, {});
        setJSON(K.profile, { ...prof, name: u.name || prof.name, term: u.term ?? prof.term });
      }
      navigate("/profil");
    } catch (e2) {
      setErr(e2.message || "Une erreur est survenue.");
      if (e2.retryAfter) setLockLeft(e2.retryAfter);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppFrame>
      <div className="px-6 pb-16 pt-10">
        <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#36302B]/45">Renaître</p>
        <h1 className="font-display mt-1 text-3xl font-medium text-[#36302B]">
          {mode === "register" ? "Créer un compte" : "Se connecter"}
        </h1>
        <p className="mt-2 text-[14px] leading-relaxed text-[#36302B]/60">
          Le compte est <strong>facultatif</strong>. Il synchronise uniquement votre prénom et le terme de bébé.
          Vos humeurs et le test d'Édimbourg restent 100% sur votre appareil.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          {mode === "register" && (
            <Field icon={User} placeholder="Votre prénom" value={name} onChange={setName} testid="auth-name" />
          )}
          <Field icon={Envelope} placeholder="Email" type="email" value={email} onChange={setEmail} testid="auth-email" required />
          <Field icon={Lock} placeholder="Mot de passe" type="password" value={password} onChange={setPassword} testid="auth-password" required />

          {err && <p data-testid="auth-error" className="text-[13px] font-semibold text-[#B56A63]">{err}</p>}
          {lockLeft > 0 && (
            <p data-testid="auth-lock-countdown" className="text-[13px] font-bold text-[#B56A63]">
              Réessayez dans {fmtMMSS(lockLeft)}
            </p>
          )}

          <button
            type="submit"
            data-testid="auth-submit"
            disabled={busy || lockLeft > 0}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#C57B67] py-4 text-[15px] font-bold text-white transition-colors hover:bg-[#B56A63] disabled:opacity-70"
          >
            {busy ? "Un instant…" : lockLeft > 0 ? `Bloqué (${fmtMMSS(lockLeft)})` : mode === "register" ? "Créer mon compte" : "Se connecter"}
            {!busy && lockLeft === 0 && <ArrowRight size={18} weight="bold" />}
          </button>
        </form>

        <button
          data-testid="auth-toggle"
          onClick={() => { setErr(""); setMode(mode === "register" ? "login" : "register"); }}
          className="mt-4 w-full text-center text-[13.5px] font-semibold text-[#C57B67]"
        >
          {mode === "register" ? "J'ai déjà un compte — me connecter" : "Pas encore de compte ? Créer un compte"}
        </button>

        <Link
          to="/"
          data-testid="auth-skip"
          className="mt-6 block rounded-full border border-[#36302B]/12 bg-white py-4 text-center text-[15px] font-bold text-[#36302B]"
        >
          Continuer sans compte
        </Link>
      </div>
    </AppFrame>
  );
}

function Field({ icon: Icon, placeholder, type = "text", value, onChange, testid, required }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border border-[#36302B]/10 bg-white px-4 py-3.5">
      <Icon size={20} color="#36302B" className="opacity-40" />
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent text-[15px] text-[#36302B] outline-none placeholder:text-[#36302B]/35"
      />
    </div>
  );
}
